import React, { useRef } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { segment } from '@actions/analytics';
import Image from '@base/Image';
import Link from 'aquilla/core/molecules/Link';
import { breakpoints, mediaQuery, Spacings } from '@styles/index';
import CMSHTMLContent from '@comp/CMSHTMLContent/CMSHTMLContent';
import { usePageSeoContent, useIsMobile } from '@stores/AppCommonData';
import Typography from 'aquilla/core/atoms/Typography';
import ContentDrawer from '../PreSubscribed/ContentDrawer';
import { ABOUT_EXAM_SEC } from './constants';
import useScrollOnMount from './hooks/useScrollOnMount';

const Wrapper = styled.div``;

const Container = styled.div`
  width: ${({ $width }) => `${$width}`};
  margin: ${Spacings.SPACING_12B} auto;
  position: relative;
  ${mediaQuery.uptoTablet} {
    width: 100%;
    padding: 0 ${Spacings.SPACING_8B};
  }
  ${mediaQuery.uptoMobile} {
    width: 100%;
    padding: 0 ${Spacings.SPACING_4B};
    margin: ${Spacings.SPACING_8B} auto;
    box-shadow: none;
    background: var(--color-base-0);
  }
`;

const Title = styled(Typography)`
  margin-bottom: ${Spacings.SPACING_3B};
`;
const SubTitle = styled(Typography)`
  max-width: 85%;
  ${mediaQuery.uptoMobile} {
    max-width: 100%;
  }
`;

const CardsContainer = styled.div`
  width: 670px;
  gap: ${Spacings.SPACING_4B};
  margin-top: ${Spacings.SPACING_6B};
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  & div:last-child {
    margin-bottom: 0;
  }
  @media only screen and (max-width: ${breakpoints.tablet}) {
    width: 328px;
    gap: ${Spacings.SPACING_6B};
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    position: relative;
    gap: ${Spacings.SPACING_6B};
    width: 100%;
  }
`;
const Card = styled.div`
  gap: 2px;
  display: flex;
  max-width: 220px;
  align-items: flex-start;
  border-radius: 6px;
  cursor: pointer;
  @media only screen and (min-width: ${breakpoints.tablet}) {
    min-height: ${Spacings.SPACING_5B};
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    min-height: ${Spacings.SPACING_5B};
  }
`;

const ImageWrapper = styled.div`
  top: 0px;
  right: 0px;
  position: absolute;
  ${mediaQuery.uptoTablet} {
    right: ${Spacings.SPACING_8B};
  }
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;
const StyledLink = styled(Link)`
  align-items: baseline;
  svg {
    width: 24px;
  }
`;
const Cards = ({ cards, setDrawerState }) => {
  const handleClick = (card) => {
    setDrawerState(card.actionText);
    segment.track('Onboarding - Goal Content Block Clicked', {
      is_logged_in: false,
      goal_content_block_type: card.description
    });
  };
  return (
    <CardsContainer>
      {cards.map((card) => (
        <Card key={card.id} onClick={() => handleClick(card)}>
          <StyledLink
            rightArrow
            underline={false}
            onClick={(e) => {
              e.preventDefault();
              setDrawerState(true);
            }}
            href="/"
          >
            <Typography variant="h6" color="var(--color-text-primary)">
              {card.title}
            </Typography>
          </StyledLink>
        </Card>
      ))}
    </CardsContainer>
  );
};

Cards.propTypes = {
  cards: PropTypes.arrayOf(PropTypes.any).isRequired,
  setDrawerState: PropTypes.func.isRequired
};
export default function AllAboutExam({ width }) {
  const isMobile = useIsMobile();
  const [drawerState, setDrawerState] = React.useState(false);
  const { contentBlock } = usePageSeoContent();
  const { title, subTitle, blocks, curtainContent } = contentBlock || {};
  const closeDrawer = () => setDrawerState(false);
  const containerRef = useRef(null);
  useScrollOnMount(ABOUT_EXAM_SEC);
  const callback = () => {
    document.getElementById(drawerState)?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return (
    <Wrapper id={ABOUT_EXAM_SEC}>
      {contentBlock?.title && (
        <Container ref={containerRef} $width={width}>
          <ImageWrapper>
            <Image
              src="all-about-exam.svg"
              height={120}
              width={120}
              alt="all-about-exam"
            />
          </ImageWrapper>
          <Title variant={isMobile ? 'h4' : 'h3'}>{title}</Title>
          <SubTitle variant="p2" color="var(--color-text-secondary)">
            {subTitle}
          </SubTitle>
          {blocks?.length > 0 && (
            <Cards cards={blocks} setDrawerState={setDrawerState} />
          )}
          <ContentDrawer
            open={!!drawerState}
            onClose={closeDrawer}
            callback={callback}
          >
            <CMSHTMLContent content={curtainContent} />
          </ContentDrawer>
        </Container>
      )}
    </Wrapper>
  );
}

AllAboutExam.propTypes = {
  width: PropTypes.string
};
AllAboutExam.defaultProps = {
  width: '1136px'
};
